import React, { useEffect, useRef, useState } from "react";
import MailMessageContent from "../../components/MailMessageContent";
import ReplyMailModal from "../../components/ReplyMailModal";
import { StatutCommandeLabels } from "../../models/commandeStatut.map";
import { ClassificationLabels } from "../../models/classification.map";
import { isFaxSender, isPharmacyAuthoredMessage, isPortalConversation } from "../../utils/commandeMail";
import PreviewAttachmentModal from "./PreviewAttachmentModal";

const PREVIEW_WIDTH_STORAGE_KEY = "commande-preview-width";
const DEFAULT_PREVIEW_WIDTH = 440;
const MIN_PREVIEW_WIDTH = 320;

function maxPreviewWidth() {
    const viewportWidth = globalThis.innerWidth || 1440;
    return Math.max(MIN_PREVIEW_WIDTH, Math.min(760, Math.round(viewportWidth * 0.62)));
}

function clampPreviewWidth(value) {
    return Math.min(Math.max(Number(value) || DEFAULT_PREVIEW_WIDTH, MIN_PREVIEW_WIDTH), maxPreviewWidth());
}

function storedPreviewWidth() {
    try {
        return clampPreviewWidth(globalThis.localStorage?.getItem(PREVIEW_WIDTH_STORAGE_KEY));
    } catch {
        return DEFAULT_PREVIEW_WIDTH;
    }
}

function formatReception(value) {
    if (!value) return "Date inconnue";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date inconnue";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month} ${hours}:${minutes}`;
}

function patientName(patient) {
    return `${patient?.prenom || ""} ${patient?.nom || ""}`.trim() || "Patient non identifié";
}

function patientInitials(patient) {
    const initials = [patient?.prenom, patient?.nom]
        .filter(Boolean)
        .map(value => value.trim().charAt(0))
        .join("")
        .toUpperCase();
    return initials || "?";
}

function patientClassification(patient) {
    if (!patient?.classification || patient.classification === "-") return null;
    return ClassificationLabels[patient.classification] || patient.classification;
}

function patientProducts(patient) {
    return patient?.medicaments ?? patient?.produits ?? [];
}

const PRODUCT_DESTINATION_ORDER = {
    ROBOT: 0,
    ETIQUETAGE: 1,
};

function groupedPatientProducts(patient) {
    return patientProducts(patient)
        .map((product, index) => ({ product, index }))
        .sort((left, right) => {
            const destinationDifference = (PRODUCT_DESTINATION_ORDER[left.product.destination] ?? 2)
                - (PRODUCT_DESTINATION_ORDER[right.product.destination] ?? 2);
            return destinationDifference || left.index - right.index;
        })
        .map(({ product }) => product);
}

function formatMessageDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

export default function CommandePreview({ commande, loading = false, onEdit, onClose, onReplySent }) {
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [showReply, setShowReply] = useState(false);
    const [previewWidth, setPreviewWidth] = useState(storedPreviewWidth);
    const [resizing, setResizing] = useState(false);
    const resizeStartRef = useRef(null);

    const updatePreviewWidth = value => setPreviewWidth(clampPreviewWidth(value));

    useEffect(() => {
        try {
            globalThis.localStorage?.setItem(PREVIEW_WIDTH_STORAGE_KEY, String(previewWidth));
        } catch {
            // La prévisualisation reste redimensionnable même si le stockage est indisponible.
        }
    }, [previewWidth]);

    useEffect(() => {
        const handleResize = () => setPreviewWidth(current => clampPreviewWidth(current));
        globalThis.addEventListener?.("resize", handleResize);
        return () => globalThis.removeEventListener?.("resize", handleResize);
    }, []);

    useEffect(() => {
        setSelectedAttachment(null);
        setShowReply(false);
    }, [commande?.id]);

    if (!commande) {
        return (
            <aside className="commande-preview commande-preview--empty" aria-label="Aperçu de la commande">
                <div className="commande-preview-empty-icon" aria-hidden="true">&#9993;</div>
                <h3>Sélectionnez un mail</h3>
                <p>Son aperçu apparaîtra ici, sans ouvrir la fiche d’édition.</p>
            </aside>
        );
    }

    const patients = commande.patients ?? [];
    const products = patients.flatMap(patientProducts);
    const robotCount = products.filter(product => product.destination === "ROBOT").length;
    const etiquetageCount = products.filter(product => product.destination === "ETIQUETAGE").length;
    const ehpadCode = commande.ehpad?.code
        || commande.ehpadCode
        || (commande.ehpadNom && commande.ehpadNom !== "AUTRE" ? commande.ehpadNom : "EHPAD non renseigné");
    const messages = commande.messages ?? [];
    const portalConversation = isPortalConversation(commande);
    const attachments = commande.files ?? (commande.fileNames ?? []).map((name, index) => ({
        name,
        id: commande.ordonnanceIds?.[index] ?? null,
        path: commande.filePaths?.[index] ?? null,
    }));
    const replyDisabled = !portalConversation && (!commande.emailEmetteur || isFaxSender(commande));

    return (
        <aside
            className={`commande-preview${resizing ? " is-resizing" : ""}`}
            aria-label="Aperçu de la commande"
            aria-busy={loading}
            style={{ width: previewWidth, minWidth: previewWidth, flexBasis: previewWidth }}
        >
            <button
                type="button"
                className="commande-preview-resizer"
                aria-label="Redimensionner l’aperçu"
                title="Glissez pour régler la largeur · Double-cliquez pour réinitialiser"
                onDoubleClick={() => updatePreviewWidth(DEFAULT_PREVIEW_WIDTH)}
                onKeyDown={event => {
                    if (event.key === "ArrowLeft") {
                        event.preventDefault();
                        updatePreviewWidth(previewWidth + 24);
                    } else if (event.key === "ArrowRight") {
                        event.preventDefault();
                        updatePreviewWidth(previewWidth - 24);
                    }
                }}
                onPointerDown={event => {
                    resizeStartRef.current = { x: event.clientX, width: previewWidth };
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    setResizing(true);
                }}
                onPointerMove={event => {
                    if (!resizeStartRef.current) return;
                    updatePreviewWidth(resizeStartRef.current.width + resizeStartRef.current.x - event.clientX);
                }}
                onPointerUp={event => {
                    resizeStartRef.current = null;
                    event.currentTarget.releasePointerCapture?.(event.pointerId);
                    setResizing(false);
                }}
                onPointerCancel={() => {
                    resizeStartRef.current = null;
                    setResizing(false);
                }}
            >
                <span aria-hidden="true" />
            </button>
            <div className="commande-preview-toolbar">
                <div className="commande-preview-toolbar-summary">
                    <span className="commande-preview-toolbar-title">Aperçu du mail</span>
                    {commande.statut && (
                        <span className={`commande-preview-status commande-preview-status--${commande.statut === "VALIDE" ? "valid" : "pending"}`}>
                            {StatutCommandeLabels[commande.statut] || commande.statut}
                        </span>
                    )}
                    <span className="commande-preview-ehpad-code" title={`Code EHPAD : ${ehpadCode}`}>
                        <strong>{ehpadCode}</strong>
                    </span>
                </div>
                <button type="button" onClick={onClose} aria-label="Fermer l’aperçu">&#215;</button>
            </div>

            <div className="commande-preview-scroll">
                {commande.favorite && <div className="commande-preview-kicker"><span className="commande-preview-favorite">&#9733; Favori</span></div>}

                {loading && <div className="commande-preview-loading">Chargement du mail…</div>}

                <dl className="commande-preview-metadata">
                    <div>
                        <dt>Reçu</dt>
                        <dd>{formatReception(commande.dateReception)}</dd>
                    </div>
                    <div>
                        <dt>Expéditeur</dt>
                        <dd>{commande.emailEmetteur || "Non renseigné"}</dd>
                    </div>
                </dl>

                <div className="commande-preview-counts" aria-label="Résumé de la commande">
                    <div><strong>{patients.length}</strong><span>patient{patients.length > 1 ? "s" : ""}</span></div>
                    <div><strong>{robotCount}</strong><span>robot</span></div>
                    <div><strong>{etiquetageCount}</strong><span>étiquetage</span></div>
                </div>

                <div className="commande-preview-thread">
                    <div className="commande-preview-section-heading">
                        <h3>Échanges</h3>
                        <div className="commande-preview-thread-actions">
                            <span>{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
                            <button
                                type="button"
                                onClick={() => setShowReply(true)}
                                disabled={replyDisabled}
                                title={replyDisabled ? "Réponse indisponible pour cet expéditeur" : (portalConversation ? "Répondre dans le portail EHPAD" : "Répondre au mail")}
                            >
                                ↩ {portalConversation ? "Répondre sur le portail" : "Répondre"}
                            </button>
                        </div>
                    </div>
                    {messages.length === 0 ? (
                        <p className="commande-preview-muted">Aucun message.</p>
                    ) : (
                        <div className="commande-preview-messages">
                            {messages.map((message, index) => {
                                const isReply = isPharmacyAuthoredMessage(message);
                                return (
                                    <article className={`commande-preview-message${isReply ? " commande-preview-message--reply" : ""}`} key={message.id ?? index}>
                                        <div className="commande-preview-message-meta">
                                            <strong>{isReply ? "Vous" : (commande.emailEmetteur || ehpadCode)}</strong>
                                            <span>{formatMessageDate(message.dateMessage)}</span>
                                        </div>
                                        <div className="commande-preview-message-body">
                                            {message.sujet && <strong className="commande-preview-message-subject">{message.sujet}</strong>}
                                            <MailMessageContent html={message.contenuHtml} text={message.contenu} />
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="commande-preview-attachments">
                    <div className="commande-preview-section-heading">
                        <h3>Pièces jointes</h3>
                        <span>{attachments.length}</span>
                    </div>
                    {attachments.length === 0 ? (
                        <p className="commande-preview-muted">Aucune pièce jointe.</p>
                    ) : (
                        <div className="commande-preview-attachment-list">
                            {attachments.map((attachment, index) => (
                                <button
                                    type="button"
                                    key={attachment.id ?? `${attachment.name}-${index}`}
                                    onClick={() => setSelectedAttachment(attachment)}
                                    aria-label={`Ouvrir la pièce jointe ${attachment.name || index + 1}`}
                                >
                                    <span aria-hidden="true">📎</span>
                                    <strong>{attachment.name || `Pièce jointe ${index + 1}`}</strong>
                                    <span>Ouvrir</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="commande-preview-assignments">
                    <div className="commande-preview-section-heading">
                        <h3>Patients et produits</h3>
                        <span>{patients.length} patient{patients.length !== 1 ? "s" : ""}</span>
                    </div>
                    {patients.length === 0 ? (
                        <p className="commande-preview-muted">Aucun patient dans cette commande.</p>
                    ) : patients.map((patient, index) => {
                        const productsForPatient = groupedPatientProducts(patient);
                        const classification = patientClassification(patient);
                        return (
                            <section className="commande-preview-patient" key={patient.commandePatientId ?? index}>
                                <div className="commande-preview-patient-heading">
                                    <span className="commande-preview-patient-avatar" aria-hidden="true">
                                        {patientInitials(patient)}
                                    </span>
                                    <div>
                                        <h4>{patientName(patient)}</h4>
                                        {classification && <span className="commande-preview-patient-classification">{classification}</span>}
                                    </div>
                                    <span className="commande-preview-patient-product-count">
                                        {productsForPatient.length} produit{productsForPatient.length !== 1 ? "s" : ""}
                                    </span>
                                </div>
                                {productsForPatient.length === 0 ? (
                                    <p className="commande-preview-muted">Aucun produit assigné</p>
                                ) : productsForPatient.map((product, productIndex) => {
                                    const destination = product.destination === "ROBOT"
                                        ? "robot"
                                        : product.destination === "ETIQUETAGE" ? "etiquetage" : "other";
                                    return (
                                        <div className={`commande-preview-product commande-preview-product--${destination}`} key={product.id ?? productIndex}>
                                            <span>{product.nom || "Produit sans nom"}</span>
                                            <small>{destination === "robot" ? "Robot" : destination === "etiquetage" ? "Étiquetage" : "Non assigné"}</small>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </div>

            </div>

            <div className="commande-preview-actions">
                <button type="button" className="commande-preview-edit" aria-label="Éditer depuis l’aperçu" onClick={() => onEdit(commande)}>
                    <span>Éditer la commande</span>
                    <kbd title="Raccourci clavier : Entrée" aria-label="Entrée">↵</kbd>
                </button>
            </div>
            {selectedAttachment && (
                <PreviewAttachmentModal attachment={selectedAttachment} onClose={() => setSelectedAttachment(null)} />
            )}
            {showReply && (
                <ReplyMailModal
                    commande={commande}
                    contained
                    onClose={sent => {
                        setShowReply(false);
                        if (sent) onReplySent?.();
                    }}
                />
            )}
        </aside>
    );
}
